import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowUp,
  ChevronDown,
  Edit,
  Loader2,
  MessageSquare,
  Save,
  Trash2,
} from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v4";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const REV_PILOT_NOTE_LABELS = [
  "General",
  "Carpenter",
  "Electrician",
  "Plumber",
  "HVAC / Mechanical",
  "Sheet Metal",
  "Controls / BAS",
  "Fire Protection (Sprinkler)",
  "Millwork",
  "Architectural Metal & Glass",
  "Audio/Visual",
  "Tele/Data",
  "Carpet/Flooring",
  "Tile/Stone",
  "Demolition",
  "Doors/Frames",
  "Other",
] as const;

export interface RevPilotNote {
  _id: Id<"revPilotChangeGroupNotes">;
  changeGroupId: Id<"revPilotChangeGroups">;
  body: string;
  label: string;
}

const NoteSchema = z.object({
  body: z.string().min(1, "Description is required"),
  label: z.string().min(1, "Trade/label is required"),
});

type NoteFormValues = z.infer<typeof NoteSchema>;

interface CompactTradeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function CompactTradeSelector({
  value,
  onChange,
  disabled,
}: CompactTradeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const isCustom = value.length > 0 && !REV_PILOT_NOTE_LABELS.includes(value as (typeof REV_PILOT_NOTE_LABELS)[number]);

  const handleSelect = (label: string) => {
    if (label === "__custom") {
      setShowCustomInput(true);
      return;
    }
    onChange(label);
    setOpen(false);
    setShowCustomInput(false);
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setOpen(false);
      setShowCustomInput(false);
      setCustomValue("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between h-7 px-2 text-xs border-dashed"
        >
          <span className={isCustom ? "" : "text-muted-foreground"}>
            {value}
          </span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        {showCustomInput ? (
          <div className="p-2 space-y-2">
            <Input
              placeholder="Enter custom label..."
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCustomSubmit();
                if (e.key === "Escape") {
                  setShowCustomInput(false);
                  setCustomValue("");
                }
              }}
              autoFocus
            />
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="xs"
                className="flex-1"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomValue("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                className="flex-1"
                onClick={handleCustomSubmit}
                disabled={!customValue.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        ) : (
          <div className="max-h-48 overflow-auto py-1">
            {REV_PILOT_NOTE_LABELS.map((label) => (
              <button
                key={label}
                onClick={() => handleSelect(label)}
                className={cn(
                  "w-full text-left px-2 py-1.5 text-xs hover:bg-muted transition-colors",
                  value === label && "bg-muted font-medium"
                )}
              >
                {label}
              </button>
            ))}
            <div className="border-t mt-1 pt-1">
              <button
                onClick={() => handleSelect("__custom")}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted transition-colors text-muted-foreground italic"
              >
                + Custom label...
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ChangeGroupNoteItemProps {
  note: RevPilotNote;
  onEdit: (note: RevPilotNote) => void;
  onDelete: (noteId: Id<"revPilotChangeGroupNotes">) => void;
  isSaving?: boolean;
}

export function ChangeGroupNoteItem({ note, onEdit, onDelete, isSaving }: ChangeGroupNoteItemProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {note.label}
        </span>
        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="icon-xs"
            className="h-5 w-5"
            aria-label="Edit note"
            onClick={() => onEdit(note)}
            disabled={isSaving}
          >
            <Edit className="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            className="h-5 w-5"
            aria-label="Delete note"
            onClick={() => onDelete(note._id)}
            disabled={isSaving}
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>
      <p className="whitespace-pre-wrap text-xs leading-relaxed">{note.body}</p>
    </div>
  );
}

interface ChangeGroupNoteEditFormProps {
  note: RevPilotNote;
  onSave: (noteId: Id<"revPilotChangeGroupNotes">, body: string, label: string) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function ChangeGroupNoteEditForm({ note, onSave, onCancel, isSaving }: ChangeGroupNoteEditFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NoteFormValues>({
    resolver: zodResolver(NoteSchema as never),
    defaultValues: {
      body: note.body,
      label: note.label,
    },
  });
  const disabled = isSaving || isSubmitting;

  const handleSave = handleSubmit((data) => {
    onSave(note._id, data.body, data.label);
  });

  return (
    <form className="space-y-2" onSubmit={handleSave}>
      <Controller
        name="label"
        control={control}
        render={({ field }) => (
          <CompactTradeSelector value={field.value} onChange={field.onChange} disabled={disabled} />
        )}
      />
      {errors.label ? (
        <p className="text-[11px] text-destructive">{errors.label.message}</p>
      ) : null}
      <textarea
        className="min-h-20 w-full rounded-md border bg-background px-2.5 py-2 text-xs resize-none"
        {...register("body")}
        disabled={disabled}
      />
      {errors.body ? (
        <p className="text-[11px] text-destructive">{errors.body.message}</p>
      ) : null}
      <div className="flex justify-end gap-1.5">
        <Button type="button" variant="outline" size="xs" onClick={onCancel} disabled={disabled} className="h-6 text-xs px-2">
          Cancel
        </Button>
        <Button type="submit" size="xs" disabled={disabled} className="h-6 text-xs px-2">
          <Save className="size-3 mr-1" />
          Save
        </Button>
      </div>
    </form>
  );
}

interface ChangeGroupNotesPanelProps {
  changeGroupId: Id<"revPilotChangeGroups">;
  included: boolean;
  notes: RevPilotNote[] | undefined;
  showHeader?: boolean;
  initialEditingNoteId?: Id<"revPilotChangeGroupNotes"> | null;
}

export function ChangeGroupNotesPanel({
  changeGroupId,
  included,
  notes,
  showHeader = false,
  initialEditingNoteId = null,
}: ChangeGroupNotesPanelProps) {
  const createNote = useMutation(api.changeGroups.createNote);
  const updateNote = useMutation(api.changeGroups.updateNote);
  const deleteNote = useMutation(api.changeGroups.deleteNote);
  const [newBody, setNewBody] = useState("");
  const [newLabel, setNewLabel] = useState("General");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditingNoteId(initialEditingNoteId ? String(initialEditingNoteId) : null);
  }, [initialEditingNoteId]);

  const handleCreateNote = async () => {
    setIsSaving(true);
    try {
      await createNote({ changeGroupId, body: newBody, label: newLabel });
      setNewBody("");
      setNewLabel("General");
      toast.success("Note added");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to add note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (note: RevPilotNote) => {
    setEditingNoteId(String(note._id));
  };

  const handleUpdateNote = async (noteId: Id<"revPilotChangeGroupNotes">, body: string, label: string) => {
    setIsSaving(true);
    try {
      await updateNote({ noteId, body, label });
      setEditingNoteId(null);
      toast.success("Note updated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: Id<"revPilotChangeGroupNotes">) => {
    setIsSaving(true);
    try {
      await deleteNote({ noteId });
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-col h-full">
      {showHeader && (
        <div className="flex items-center justify-between border-b px-4 py-3 bg-card">
          <div className="flex items-center gap-2">
            <MessageSquare className="size-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">Narrative Notes</h4>
          </div>
          <Badge variant="outline" className="text-xs">
            {notes?.length ?? 0}
          </Badge>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {notes === undefined ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Loading notes...
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">No notes yet.</p>
        ) : (
          notes.map((note) => {
            const isEditing = editingNoteId === String(note._id);
            return (
              <div key={note._id} className="rounded-md border bg-background/80 p-2">
                {isEditing ? (
                  <ChangeGroupNoteEditForm
                    note={note}
                    onSave={handleUpdateNote}
                    onCancel={() => setEditingNoteId(null)}
                    isSaving={isSaving}
                  />
                ) : (
                  <ChangeGroupNoteItem
                    note={note}
                    onEdit={handleStartEdit}
                    onDelete={handleDeleteNote}
                    isSaving={isSaving}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-2 border-t p-3 bg-card">
        {!included ? (
          <div className="rounded-md bg-muted p-2">
            <p className="text-xs font-medium">Excluded change</p>
            <p className="text-xs text-muted-foreground">
              Adding notes is disabled while this change group is excluded.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Trade/Label</span>
              </div>
              <CompactTradeSelector value={newLabel} onChange={setNewLabel} disabled={isSaving} />
            </div>
            <div className="space-y-1.5">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Description</span>
              <div className="relative">
                <textarea
                  className="min-h-28 w-full rounded-md border bg-background px-2.5 py-2 pr-9 pb-10 text-xs resize-none"
                  placeholder="Describe this change..."
                  value={newBody}
                  onChange={(event) => setNewBody(event.target.value)}
                  disabled={isSaving}
                />
                <Button
                  size="icon-xs"
                  className="absolute bottom-3 right-2 h-5 w-5"
                  aria-label="Add note"
                  onClick={() => void handleCreateNote()}
                  disabled={isSaving || !newBody.trim() || !newLabel.trim()}
                >
                  <ArrowUp className="size-3" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
